import { useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import ReactGA from 'react-ga';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import CreateProfile from './components/createProfile/CreateProfile';
import { auth } from './firebase';
import { useMeQuery } from './generated/graphql';
import Profile, { ViewType } from './profile/Profile';
import ProfileRedirect from './profile/ProfileRedirect';
import Dashboard from './student/dashboard/Dashboard';
import JobBoard from './student/jobs/JobBoard';
import JobPost from './student/jobs/JobPost';
import CreateAccount from './student/login/CreateAccount';
import Login from './student/login/Login';
import ResetPassword from './student/login/ResetPassword';

const TRACKING_ID = 'UA-144264697-3';
ReactGA.initialize(TRACKING_ID);

function App() {
    const [student] = useAuthState(auth);
    const [firebaseToken, setFirebaseToken] = useState<string | undefined>();

    useEffect(() => {
        const getFirebaseToken = async () => {
            const id = await student
                ?.getIdToken(true)
                .then(function (idToken) {
                    sessionStorage.setItem('accessToken', idToken ? idToken : '');
                    setFirebaseToken(idToken);
                })
                .catch(function (error) {
                    // Handle error
                    console.log({ error });
                });
        };
        getFirebaseToken();

    });
    const { data: userData, loading: isLoading } = useMeQuery({
        variables: {}
    });

    if (isLoading) return <div>Loading...</div>;

    return (
        <div className="App">
            <Router>
                <LogLocations />
                <Routes>
                    {/* Login Functionality. */}
                    <Route path="/" element={<Navigate replace to="/login" />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<CreateAccount />} />
                    <Route path="/reset" element={<ResetPassword />} />
                    {/* <Route path="/action" element={<AuthenticationAction />} /> */}
                    {/* For logged in students. */}
                    <Route
                        path="/createProfile"
                        element={<RequireAuth userData={userData} studentPage={<CreateProfile/>}></RequireAuth>}/>

                    <Route
                        path="/dashboard"
                        element={<RequireAuth userData={userData} studentPage={<Dashboard />}></RequireAuth>}
                    />
                    <Route
                        path="/profile"
                        element={<RequireAuth userData={userData} studentPage={<ProfileRedirect />}></RequireAuth>}
                    />
                    <Route
                        path="/job-board/:jobId"
                        element={
                            <RequireAuth userData={userData}
                                studentPage={<JobPost studentView={true} />}
                                defaultPage={<JobPost studentView={false} />}
                            ></RequireAuth>
                        }
                    />
                    <Route
                        path="/job-board"
                        element={<RequireAuth userData={userData} studentPage={<JobBoard />}></RequireAuth>}
                    />
                    {/* Shared across multiple versions of the product. */}
                    <Route
                        path="/profile/:profileSlug"
                        element={
                            <RequireAuth userData={userData}
                                studentPage={<Profile view={ViewType.Student} />}
                                defaultPage={<Profile view={ViewType.LoggedOut} />}
                            ></RequireAuth>
                        }
                    />


                    {/* Admin Functionality. */}
                    <Route
                        path="/profile/admin/:profileSlug"
                        element={<Profile view={ViewType.Admin} />}
                    />
                </Routes>
            </Router>
        </div>
    );
}

// First preference is to login and show the corresponding page.  If no pages
// can be provided, then redirect to the appropriate login page.
function RequireAuth({
    userData,
    /* If a student is logged in, go to the student page. (preferred) */
    studentPage,
    /* If student is not logged in. */
    defaultPage = <Navigate to="/login" />
}: {
    userData: any;
    studentPage?: JSX.Element;
    defaultPage?: JSX.Element;
}) {
    let location = useLocation();
    const [student, loading] = useAuthState(auth);

    if (studentPage && loading) {
        return <div>Loading..</div>;
    }

    // Render default page if provided and no student or employer logged in.
    if (!student) {
        return defaultPage;
    }

    // The student is logged in.
    if (studentPage && student) {
        return studentPage;
    }

    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/login" state={{ from: location }} />;
}

function LogLocations() {
    let location = useLocation();
    useEffect(() => {
        ReactGA.pageview(location.pathname);
    }, [location]);
    return <></>;
}

export default App;
